/**
 * Helpers for the trader funds-page back-button + dialog interaction.
 *
 * Problem: every Radix dialog on `/trade/funds` (Add funds, Withdrawal
 * details, "Action blocked" info, etc.) renders a `fixed inset-0` overlay
 * at `z-[100]` plus a centered DialogContent panel — also at `z-[100]`.
 * The page header back chevron sits beneath both, so a tap aimed at the
 * back button lands on the overlay (small dialog) OR on the dialog
 * content itself (large dialog like the withdrawal view with a proof
 * image). Radix closes the dialog via `onPointerDownOutside` only for
 * the first case; for the second case the click hits the content panel
 * and nothing happens. Either way the back button's `onClick` never
 * fires, and the user has to tap twice.
 *
 * Two-part fix:
 *
 * 1. Bump the page header back button to `z-[110]` and `relative` so it
 *    sits hit-test above the dialog's `z-[100]` overlay AND content. The
 *    button receives the click directly — no Radix interception, no
 *    overlay/content occlusion. See the back button in
 *    [trade/funds/page.tsx](../app/trade/funds/page.tsx). The lightbox
 *    at `z-[200]` still sits above the button (intentional — it's a
 *    full-screen viewer); its own click handler forwards to the button
 *    via `forwardPageBackIfHitAtPoint` below.
 *
 *    Being *hit-test* above the dialog doesn't mean it should be
 *    *visually* above it too — a solid chevron floating on top of a
 *    dialog's title (or glowing against the dim overlay of a small one)
 *    reads as a stray control the modal forgot to cover. `useFundsPageBackHandler`
 *    below tracks how many funds-page overlays are currently open and
 *    fades the real button to `opacity: 0` for the duration — the hit
 *    target and its z-110 stacking are untouched, so the single-tap
 *    close-and-navigate flow keeps working; only the visible leak is
 *    gone.
 *
 * 2. The back button's `onClick` dispatches a window-level
 *    `funds-page-back` CustomEvent BEFORE calling `router.back()`. Every
 *    funds-page overlay (AddFunds, withdraw View, BlockDialog,
 *    ImageLightbox) listens via `useFundsPageBackHandler` and closes
 *    itself synchronously when the event fires. Net result: a single tap
 *    closes every stacked overlay AND navigates back.
 *
 * `forwardPageBackIfHitAtPoint` is kept for the lightbox case: while the
 * lightbox is open it covers the back button (z-200), so its backdrop
 * onClick needs to detect a hit on the back-button rect and dispatch a
 * programmatic click after `onClose` so the closure-then-navigate flow
 * collapses to one tap.
 */

import { useEffect, useRef } from "react"

// Ref-count of currently-open funds-page overlays (dialogs + lightbox).
// Multiple overlays can be open at once (e.g. the withdrawal-detail
// dialog with its proof-image lightbox on top), so visibility is driven
// by a counter, not a single boolean — the button only comes back into
// view once every overlay that hid it has closed.
let openOverlayCount = 0

function syncBackButtonVisibility() {
  if (typeof document === "undefined") return
  const btn = document.querySelector<HTMLButtonElement>("[data-page-back]")
  if (!btn) return
  btn.style.opacity = openOverlayCount > 0 ? "0" : ""
}

// Per-tick dedup for the synthesized back-button click. Lightbox close
// flows can fire onPointerDown twice in quick succession (once from the
// X button, once from the backdrop); without the guard we'd rocket the
// user back two history entries instead of one.
let lastDispatchTs = 0

/** Custom event broadcast by the funds-page back button on click. Every
 *  overlay on the page listens and closes itself before navigation runs. */
export const FUNDS_PAGE_BACK_EVENT = "funds-page-back"

/**
 * Hit-test (clientX, clientY) against the page back button rect and, on
 * a match, schedule a programmatic click on the button. Used by the
 * lightbox (z-200, sits above the back button) to translate a tap on
 * the back-button area into a real navigation after `onClose` runs.
 */
export function forwardPageBackIfHitAtPoint(clientX: number, clientY: number): boolean {
  if (typeof document === "undefined") return false
  const btn = document.querySelector<HTMLButtonElement>("[data-page-back]")
  if (!btn) return false
  const rect = btn.getBoundingClientRect()
  if (rect.width === 0 || rect.height === 0) return false
  if (clientX < rect.left || clientX > rect.right) return false
  if (clientY < rect.top || clientY > rect.bottom) return false
  const now = (typeof performance !== "undefined" ? performance.now() : Date.now())
  if (now - lastDispatchTs < 250) return true
  lastDispatchTs = now
  setTimeout(() => {
    try { btn.click() } catch { /* noop */ }
  }, 0)
  return true
}

/**
 * Bind an overlay's close handler to the funds-page back-button
 * broadcast. Every funds-page dialog/lightbox uses this so a single tap
 * on the header back chevron closes the whole overlay stack AND lets
 * `router.back()` run cleanly on the same tap.
 *
 * Pass the same `open` and `onClose` props the overlay already uses —
 * the listener auto-removes when the overlay closes or unmounts.
 */
export function useFundsPageBackHandler(open: boolean, onClose: () => void) {
  // Stable ref so the effect only depends on `open`. Callers typically
  // pass an inline arrow `() => setX(null)` whose identity changes on
  // every parent render — the funds page re-renders constantly from WS
  // tick / balance updates. Putting onClose in deps would attach/detach
  // the listener on every render; harmless here but the same pattern in
  // ImageLightbox's open-effect cleanup pops a history entry and silently
  // auto-closed the lightbox. Defending here too prevents that class of
  // bug from showing up in any future overlay that adopts this hook.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  useEffect(() => {
    if (!open || typeof window === "undefined") return
    openOverlayCount++
    syncBackButtonVisibility()
    const handler = () => onCloseRef.current()
    window.addEventListener(FUNDS_PAGE_BACK_EVENT, handler)
    return () => {
      window.removeEventListener(FUNDS_PAGE_BACK_EVENT, handler)
      openOverlayCount = Math.max(0, openOverlayCount - 1)
      syncBackButtonVisibility()
    }
  }, [open])
}

/** Dispatch the broadcast. Called from the page back button's `onClick`. */
export function emitFundsPageBack() {
  if (typeof window === "undefined") return
  window.dispatchEvent(new CustomEvent(FUNDS_PAGE_BACK_EVENT))
}

/**
 * Sentinel key used to remember that the trader landed on
 * `/trade/funds/withdraw` from `/trade/funds` (the Withdraw button or
 * the rejected-withdrawal "Retry" button). After a successful submit
 * (or a "Back to Funds" tap on the gate page), the withdraw screen
 * checks this flag and calls `router.back()` instead of
 * `router.replace("/trade/funds")`.
 *
 * Why: `router.replace` swaps the withdraw entry for a funds entry,
 * BUT a funds entry already sits beneath it (the one the trader
 * pushed FROM). The stack ends up `[..., profile, funds, funds]`, so
 * the first back tap pops a duplicate funds entry — URL doesn't change
 * — and the trader has to tap a second time to actually leave the
 * funds page. `router.back()` instead unwinds the withdraw entry and
 * lands on the original funds entry, leaving the stack flat.
 *
 * Fallback: if the flag is missing (trader deep-linked straight to
 * `/trade/funds/withdraw` from outside the app, e.g. a notification
 * tap), use `router.replace` so they still end up on the funds page.
 */
const WITHDRAW_FROM_FUNDS_KEY = "trade-funds-withdraw-from-funds"

export function markWithdrawNavFromFunds() {
  if (typeof window === "undefined") return
  try { sessionStorage.setItem(WITHDRAW_FROM_FUNDS_KEY, "1") } catch { /* noop */ }
}

export function popWithdrawNavFromFunds(): boolean {
  if (typeof window === "undefined") return false
  try {
    const flag = sessionStorage.getItem(WITHDRAW_FROM_FUNDS_KEY) === "1"
    if (flag) sessionStorage.removeItem(WITHDRAW_FROM_FUNDS_KEY)
    return flag
  } catch { return false }
}

// Same pattern for the manual-deposit page. Trader arrives from
// /trade/funds via router.push, so a post-submit router.back() unwinds
// the manual-deposit entry cleanly instead of leaving an adjacent
// duplicate `funds` entry that swallows the first back tap.
const MD_FROM_FUNDS_KEY = "trade-funds-md-from-funds"

export function markManualDepositNavFromFunds() {
  if (typeof window === "undefined") return
  try { sessionStorage.setItem(MD_FROM_FUNDS_KEY, "1") } catch { /* noop */ }
}

export function popManualDepositNavFromFunds(): boolean {
  if (typeof window === "undefined") return false
  try {
    const flag = sessionStorage.getItem(MD_FROM_FUNDS_KEY) === "1"
    if (flag) sessionStorage.removeItem(MD_FROM_FUNDS_KEY)
    return flag
  } catch { return false }
}
